import { Inject, Injectable, NgZone } from '@angular/core';
import { Device } from '@ionic-native/device/ngx';
import { UUID } from 'angular2-uuid';
import { BehaviorSubject } from 'rxjs';
import { TerminalDataService } from '../private/terminal/services/terminal-data.service';
import { TerminalAuthService } from '../services/terminal-auth.service';
import { DeviceWsConnector } from './device-ws.connector';
import { PosCommandEnvelope } from './pos-command.models';
import { PosTerminalCommunicationService } from './pos-terminal-communication.service';

export type SupportStatus =
  | 'idle'
  | 'sending'
  | 'waiting'
  | 'connected'
  | 'rejected'
  | 'failed'
  | 'expired';

export interface SupportState {
  status: SupportStatus;
  requestId?: string;
  // What the user wrote in the support form; sent again on retry.
  message?: string;
  // The admin who accepted the request or connected (the command's issuedBy).
  operator?: string;
  since?: string;
  error?: string;
}

// Badge corners, in the order the reposition button goes through them.
export const SUPPORT_BADGE_CORNERS = ['top-right', 'top-left', 'bottom-left', 'bottom-right'] as const;
export type SupportBadgeCorner = typeof SUPPORT_BADGE_CORNERS[number];

const STATE_KEY = 'pos_support_session';
const CORNER_KEY = 'pos_support_badge_corner';
// Erp.Ws drops a request nobody answered after 30 minutes.
const WAIT_LIMIT_MS = 30 * 60 * 1000;
// Pos.Ws keeps this much of the message.
export const SUPPORT_MESSAGE_MAX = 500;

// Support connection with the admin tool: the support window sends a request, an admin accepts or
// rejects it and the answer comes as the "supportSession" command. While connected,
// SupportBadgeComponent shows it over every window; a connection without a request shows nothing.
@Injectable({ providedIn: 'root' })
export class PosTerminalSupportService {
  private readonly _state = new BehaviorSubject<SupportState>(this._readState());
  private readonly _corner = new BehaviorSubject<SupportBadgeCorner>(this._readCorner());
  private _waitTimer?: ReturnType<typeof setTimeout>;

  readonly state$ = this._state.asObservable();
  readonly corner$ = this._corner.asObservable();

  constructor(
    communication: PosTerminalCommunicationService,
    private _connector: DeviceWsConnector,
    private _device: Device,
    private _terminalAuth: TerminalAuthService,
    private _terminalData: TerminalDataService,
    private _zone: NgZone,
    @Inject('ENVIRONMENT') private _environment: { version?: string },
  ) {
    communication.register('supportSession', (payload, envelope) =>
      this._zone.run(() => this._onSession(payload, envelope)),
    );
  }

  get state(): SupportState {
    return this._state.value;
  }

  // Sends the support form's request unless one is already waiting or connected.
  async requestSupport(message?: string): Promise<void> {
    const current = this.state.status;
    if (current === 'sending' || current === 'waiting' || current === 'connected') {
      return;
    }

    const requestId = UUID.UUID();
    const text = (message ?? this.state.message ?? '').trim().slice(0, SUPPORT_MESSAGE_MAX) || undefined;
    this._set({ status: 'sending', requestId, message: text });
    try {
      await this._connector.invokeOrFail('RequestSupport', {
        requestId,
        message: text,
        storeName: this._safe(() => {
          const store = this._terminalData.terminalSettings()?.store;
          return store?.name || store?.erpName;
        }),
        employeeName: this._safe(() => this._terminalAuth.currentUser?.full_name),
        appVersion: this._environment?.version,
        platform: this._device.platform,
      });
      // Only if no answer arrived while the request was being sent.
      if (this.state.requestId === requestId && this.state.status === 'sending') {
        this._set({ status: 'waiting', requestId, message: text, since: new Date().toISOString() });
        this._startWaitTimer(requestId);
      }
    } catch (err) {
      if (this.state.requestId === requestId) {
        this._set({ status: 'failed', requestId, message: text, error: this._message(err) });
      }
    }
  }

  // Withdraws a request nobody has answered yet.
  async cancelRequest(): Promise<void> {
    const { status, requestId } = this.state;
    if (status !== 'waiting' && status !== 'sending') {
      return;
    }

    this._set({ status: 'idle' });
    try {
      await this._connector.invokeOrFail('CancelSupport', requestId);
    } catch (err) {
      console.error('Support request was not cancelled on the server', err);
    }
  }

  // The admin ended the support session another way (closed its chat): the badge goes.
  endSession(): void {
    if (this.state.status === 'connected') {
      this._set({ status: 'idle', operator: this.state.operator });
    }
  }

  // Back to idle after a rejection, failure or expiry (the support window shows the result until then).
  reset(): void {
    if (this.state.status !== 'connected') {
      this._set({ status: 'idle' });
    }
  }

  moveBadge(): void {
    const next = SUPPORT_BADGE_CORNERS[(SUPPORT_BADGE_CORNERS.indexOf(this._corner.value) + 1) % SUPPORT_BADGE_CORNERS.length];
    this._corner.next(next);
    try {
      localStorage.setItem(CORNER_KEY, next);
    } catch {
      // the badge goes back to its default corner after a reload
    }
  }

  private _onSession(payload: { state?: string; requestId?: string }, envelope: PosCommandEnvelope) {
    const state = payload?.state;
    const operator = envelope?.issuedBy;

    // Only answers to this terminal's own request count: an admin who connects without a request
    // (or answers an old one) gets the reply, but the badge is not shown.
    const ownRequest = !!payload?.requestId && payload.requestId === this.state.requestId;

    if (state === 'connected') {
      const pending = this.state.status === 'sending' || this.state.status === 'waiting';
      if (ownRequest && (pending || this.state.status === 'connected')) {
        this._set({ status: 'connected', requestId: payload.requestId, operator, since: this.state.since || new Date().toISOString() });
      }
    } else if (state === 'rejected') {
      if (ownRequest && this.state.status !== 'connected') {
        this._set({ status: 'rejected', requestId: payload.requestId, operator });
      }
    } else if (state === 'disconnected') {
      if (ownRequest) {
        this._set({ status: 'idle', operator });
      }
    } else {
      throw new Error(`Unknown support session state "${state}"`);
    }

    return { status: this.state.status };
  }

  private _startWaitTimer(requestId: string) {
    clearTimeout(this._waitTimer);
    this._waitTimer = setTimeout(() => {
      if (this.state.status === 'waiting' && this.state.requestId === requestId) {
        this._set({ status: 'expired', requestId });
      }
    }, WAIT_LIMIT_MS);
  }

  private _set(state: SupportState) {
    if (state.status !== 'waiting') {
      clearTimeout(this._waitTimer);
    }

    this._state.next(state);
    try {
      // Only a connected session survives a reload (the badge stays); a request in flight does not.
      if (state.status === 'connected') {
        localStorage.setItem(STATE_KEY, JSON.stringify(state));
      } else {
        localStorage.removeItem(STATE_KEY);
      }
    } catch {
      // without storage the badge is gone after a reload
    }
  }

  private _readState(): SupportState {
    try {
      const stored = JSON.parse(localStorage.getItem(STATE_KEY));
      return stored?.status === 'connected' ? stored : { status: 'idle' };
    } catch {
      return { status: 'idle' };
    }
  }

  private _readCorner(): SupportBadgeCorner {
    try {
      const stored = localStorage.getItem(CORNER_KEY) as SupportBadgeCorner;
      return SUPPORT_BADGE_CORNERS.includes(stored) ? stored : 'top-right';
    } catch {
      return 'top-right';
    }
  }

  private _safe<T>(read: () => T): T | undefined {
    try {
      return read();
    } catch {
      return undefined;
    }
  }

  private _message(err: unknown): string {
    return (err as Error)?.message || String(err) || 'Error';
  }
}
